import type { PlaybackInfo } from '@/features/live/types';
import type {
  EvergreenConfiguration,
  EvergreenInteractionOutsideVideo,
  WebinarSessionConfiguration,
} from '@/features/webinar/types';

/**
 * Le MP4 importé prime sur l'enregistrement du Live source : un Live diffusé sur YouTube n'a pas
 * d'enregistrement Welya.
 */
export function resolveEvergreenPreviewVideo({
  importedVideoUrl,
  hasSourceSession,
  playbackInfo,
}: {
  importedVideoUrl: string;
  hasSourceSession: boolean;
  playbackInfo: Pick<PlaybackInfo, 'mp4Url' | 'fallbackVideoUrl'> | null | undefined;
}): { videoUrl: string; videoFallbackUrl: string | undefined } {
  if (importedVideoUrl) return { videoUrl: importedVideoUrl, videoFallbackUrl: undefined };
  if (!hasSourceSession) return { videoUrl: '', videoFallbackUrl: undefined };
  return {
    videoUrl: playbackInfo?.mp4Url ?? '',
    videoFallbackUrl: playbackInfo?.fallbackVideoUrl ?? undefined,
  };
}

export type LiveSourceVideoCard =
  { kind: 'importedMp4'; videoName: string } | { kind: 'liveRecording' } | { kind: 'none' };

/**
 * Carte vidéo d'un Evergreen issu d'un Live, porteuse du bouton « Remplacer ». L'enregistrement du
 * Live n'est remplaçable qu'une fois la conversion terminée : avant, la timeline est incomplète et
 * le contrôle « vidéo trop courte » ne vaut rien.
 */
export function resolveLiveSourceVideoCard({
  evergreenConfiguration,
  mp4Required,
}: {
  evergreenConfiguration: Pick<EvergreenConfiguration, 'videoName' | 'durationSeconds'> | null;
  mp4Required: boolean;
}): LiveSourceVideoCard {
  if (evergreenConfiguration == null) return { kind: 'none' };
  if (evergreenConfiguration.videoName) {
    return { kind: 'importedMp4', videoName: evergreenConfiguration.videoName };
  }
  if (mp4Required || evergreenConfiguration.durationSeconds == null) return { kind: 'none' };
  return { kind: 'liveRecording' };
}

/**
 * Créneaux éditables sur tout Evergreen, quelle que soit sa source. Tant que la durée de la vidéo
 * est inconnue (MP4 pas encore importé, conversion en cours), le back refuse l'enregistrement.
 */
export function canScheduleEvergreenInteractions(
  sessionConfiguration: Pick<WebinarSessionConfiguration, 'type'> | null | undefined,
): boolean {
  return sessionConfiguration?.type === 'EVERGREEN';
}

export type EvergreenConversionOutcome =
  | { kind: 'ready' }
  | { kind: 'blocked'; interactions: EvergreenInteractionOutsideVideo[] }
  | { kind: 'pending' }
  | { kind: 'unavailable' };

type ConversionProgress = Pick<
  EvergreenConfiguration,
  'ready' | 'conversionTotalItems' | 'conversionProcessedItems' | 'interactionsOutsideVideo'
>;

/** Une conversion terminée mais non prête est bloquée par des interactions hors vidéo. */
export function resolveConversionOutcome(
  configuration: ConversionProgress,
): EvergreenConversionOutcome {
  if (configuration.ready) return { kind: 'ready' };
  const interactions = configuration.interactionsOutsideVideo ?? [];
  const processed = configuration.conversionProcessedItems >= configuration.conversionTotalItems;
  return processed && interactions.length > 0
    ? { kind: 'blocked', interactions }
    : { kind: 'pending' };
}

type WaitOptions = {
  loadConfiguration: () => Promise<ConversionProgress>;
  signal?: AbortSignal;
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
};

const CONVERSION_POLL_INTERVAL_MS = 2_000;
// Au-delà, la conversion continue côté serveur et publie l'Evergreen seule : on rend la main.
const CONVERSION_MAX_WAIT_MS = 5 * 60_000;

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function waitForEvergreenConversion({
  loadConfiguration,
  signal,
  intervalMs = CONVERSION_POLL_INTERVAL_MS,
  timeoutMs = CONVERSION_MAX_WAIT_MS,
  sleep = defaultSleep,
  now = Date.now,
}: WaitOptions): Promise<EvergreenConversionOutcome> {
  const deadline = now() + timeoutMs;
  while (!signal?.aborted) {
    // Une erreur passagère (réseau, 5xx, jeton expiré) ne dit rien de la conversion : on réessaie.
    const outcome = await loadConfiguration().then(
      resolveConversionOutcome,
      (): EvergreenConversionOutcome => ({ kind: 'unavailable' }),
    );
    if (outcome.kind === 'ready' || outcome.kind === 'blocked' || now() >= deadline) {
      return outcome;
    }
    await sleep(intervalMs);
  }
  return { kind: 'pending' };
}
